import React from 'react'
import { AlertTriangle, Play, XCircle, X } from 'lucide-react'

const ActiveChallengeWarningModal = ({
  isOpen,
  onClose,
  onResume,
  onCancel,
  challengeData,
  isProcessing = false
}) => {
  if (!isOpen) return null

  const { challengeName, currentStageName, completedStages, totalStages } = challengeData || {}

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="bg-orange-100 rounded-full p-3">
              <AlertTriangle className="w-8 h-8 text-orange-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Active Challenge Detected</h2>
              <p className="text-sm text-gray-600">Cannot start a new challenge</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Active Challenge Info */}
        {challengeData && (
          <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-2">Current Active Challenge</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Challenge:</span>
                <span className="text-sm font-medium text-gray-900">{challengeName}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Current Stage:</span>
                <span className="text-sm font-medium text-gray-900">{currentStageName}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Progress:</span>
                <span className="text-sm font-medium text-gray-900">
                  {completedStages} of {totalStages} stages completed
                </span>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
              <div
                className="bg-orange-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(completedStages / totalStages) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Warning Message */}
        <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-orange-800 mb-1">
                Cannot Start New Challenge
              </h3>
              <p className="text-sm text-orange-700">
                You have an active challenge that must be completed or cancelled before starting a
                new one. Please resume your current challenge or cancel it to proceed.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={onResume}
            disabled={isProcessing}
            className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Loading...
              </>
            ) : (
              <>
                <Play className="w-5 h-5 mr-2" />
                Resume Active Challenge
              </>
            )}
          </button>

          <button
            onClick={onCancel}
            disabled={isProcessing}
            className="w-full bg-red-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <XCircle className="w-5 h-5 mr-2" />
            Cancel Active Challenge
          </button>

          <button
            onClick={onClose}
            disabled={isProcessing}
            className="w-full bg-gray-100 text-gray-600 py-2 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default ActiveChallengeWarningModal
